import { HeroIcon } from '@components/HeroIcon'
import { ILesson } from '@dtos/Course'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useContentsModalStore } from '@stores/useContentsModalStore'
import { useLessonTabStore } from '@stores/useLessonTabStore'
import { LessonTypeTranslation } from '@utils/lessonMediaType'
import { IVideoThumbMedia, getVideoThumb } from '@utils/videoThumbs'
import { router } from 'expo-router'
import { useEffect, useMemo, useRef, useCallback } from 'react'
import { View, Text, XStack, Image, YStack, Stack } from 'tamagui'
import { useScrollBarStore } from '@stores/useScrollBarStore'
import { mediaDuration } from '@utils/mediaDurationFormatter'
import { useCourse } from '@hooks/useCourse'

interface ILessonHorizontalCardProps {
  dataLesson: ILesson
  isAvailable: boolean
  availableLabel: string
  hasNoContent?: boolean
  courseContentsId: number
  moduleId: number
  media: IVideoThumbMedia
  isCompleted?: boolean
}

export function LessonHorizontalCard({
  dataLesson,
  isAvailable,
  availableLabel,
  hasNoContent,
  courseContentsId,
  moduleId,
  media,
  isCompleted,
}: ILessonHorizontalCardProps) {
  const lessonPositionRef = useRef(0)
  const scrollTimeoutRef = useRef<NodeJS.Timeout>()
  const { isOpen, setPosition } = useContentsModalStore()
  const { course } = useCourse()
  const { scrollTo } = useScrollBarStore()
  const { currentLesson, setCurrentLesson } = useCurrentLesson()
  const setCurrentTab = useLessonTabStore((state) => state.setCurrentTab)
  const isSelected = currentLesson?.id === dataLesson.id
  // TODO: Mockado por enquanto pois o back irá trazer se pode avançar ou não
  const isPreviousLessonCompleted = true
  const courseHasLinearRequirement = course?.linearRequirements
  const displayLinearRequirementText =
    !isAvailable &&
    !isCompleted &&
    courseHasLinearRequirement &&
    isPreviousLessonCompleted
  const selectedBackground = isSelected ? '$neutral-100' : '$neutral-0'
  const selectedBorderColor = isSelected ? '$neutral-200' : '$neutral-0'
  const lessonThumbnail = getVideoThumb(media)
  const isYoutubeOrVimeo = ['Youtube', 'Vimeo'].includes(media.host || '')
  const displayThumbnail = media.coverImage || isYoutubeOrVimeo
  const lessonTitle = hasNoContent
    ? 'Em breve esse conteúdo será adicionado'
    : dataLesson.title
  const lessonIconColor = hasNoContent ? '$neutral-800' : '$primary-300'
  const lessonIcon = hasNoContent
    ? ['fal', 'hourglass']
    : LessonTypeTranslation(
        dataLesson.activity ? 'Quiz' : dataLesson.media?.type || 'Text',
      )

  const handleScroll = useCallback(
    (y: number) => {
      if (y <= 0) return

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      scrollTimeoutRef.current = setTimeout(() => {
        scrollTo('module-scroll', y)
      }, 300)
    },
    [scrollTo],
  )

  useEffect(() => {
    if (isOpen && isSelected && lessonPositionRef.current > 0) {
      handleScroll(lessonPositionRef.current)
    }
  }, [isOpen, isSelected, handleScroll])

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const Section = useCallback(
    ({
      icon,
      text,
      iconColor,
    }: {
      icon: IconProp
      text: string
      iconColor: string
    }) => {
      return (
        <>
          <HeroIcon icon={icon} size={14} color={iconColor} />
          <Text
            color="$neutral-800"
            fontFamily="$mulishSemiBold"
            fontWeight="600"
            fontSize="$font-size-xxxs"
          >
            {text}
          </Text>
        </>
      )
    },
    [],
  )

  const renderLessonStatus = useMemo(() => {
    if (isAvailable && dataLesson.media?.duration && !isCompleted) {
      return (
        <XStack
          paddingHorizontal={8}
          paddingVertical={4}
          backgroundColor="$neutral-200"
          borderRadius="$border-radius-sm"
        >
          <Text
            ff="$mulishSemiBold"
            fontSize="$font-size-xxxs"
            color="$neutral-800"
            fontWeight="600"
          >
            {mediaDuration(dataLesson.media.duration)}
          </Text>
        </XStack>
      )
    }

    if (isCompleted) {
      return (
        <Section
          icon={['fas', 'check-circle']}
          text="Item concluído"
          iconColor="$success-300"
        />
      )
    }

    if (availableLabel) {
      return (
        <Section
          icon={['fal', 'calendar-alt']}
          text={availableLabel}
          iconColor="$neutral-500"
        />
      )
    }
    if (displayLinearRequirementText) {
      return (
        <Section
          icon={['fal', 'exclamation-circle']}
          text={'Conclua a aula anterior'}
          iconColor="$neutral-800"
        />
      )
    }
    return null
  }, [
    isAvailable,
    dataLesson.media?.duration,
    isCompleted,
    availableLabel,
    displayLinearRequirementText,
    Section,
  ])

  const handlePress = () => {
    if (!isAvailable) return
    router.setParams({
      courseContentsId: courseContentsId.toString(),
      moduleId: moduleId.toString(),
    })
    setPosition(1)
    setCurrentLesson(dataLesson)
    setCurrentTab('description')
  }

  return (
    <View
      backgroundColor={selectedBackground}
      br="$border-radius-md"
      borderWidth={1}
      testID="lesson-horizontal-card"
      borderColor={selectedBorderColor}
      marginBottom="$spacing-inset-xs"
      position="relative"
      onPress={handlePress}
      overflow="visible"
      marginLeft={16}
      onLayout={(event) => {
        const y = event.nativeEvent.layout.y
        if (y > 0 && isSelected) {
          lessonPositionRef.current = y
        }
      }}
    >
      {isSelected && (
        <Stack
          position="absolute"
          left={0}
          top="50%"
          zIndex={999}
          overflow="visible"
          style={{ overflow: 'visible' }}
          pointerEvents="none"
          transform={[{ translateX: -10 }, { translateY: -9 }]}
        >
          <HeroIcon icon={['fas', 'play']} size={16} color="$primary-300" />
        </Stack>
      )}
      <XStack
        gap="$spacing-inset-nano"
        padding="$spacing-inset-nano"
        alignItems="center"
      >
        <View>
          <View>
            {!isAvailable && (
              <Stack
                bg="$warning-300"
                borderRadius="$border-radius-pill"
                position="absolute"
                zIndex={100}
                padding={8}
                right="50%"
                top="50%"
                transform={[{ translateX: 15 }, { translateY: -16 }]}
              >
                <HeroIcon
                  icon={['fal', 'lock-keyhole']}
                  color="$neutral-0"
                  size={16}
                />
              </Stack>
            )}
            {displayThumbnail ? (
              <Image
                testID="lesson-thumbnail"
                source={{
                  uri: lessonThumbnail || undefined,
                }}
                width={82}
                height={47}
                alt={dataLesson.title}
                br="$border-radius-sm"
              />
            ) : (
              <XStack
                br="$border-radius-sm"
                bg="$background-100"
                testID="lesson-icon"
                alignItems="center"
                justifyContent="center"
                height={47}
                width={82}
              >
                <HeroIcon
                  icon={lessonIcon as IconProp}
                  color={lessonIconColor}
                  size={16}
                />
              </XStack>
            )}
          </View>
        </View>
        <YStack
          justifyContent="space-between"
          gap="$spacing-inset-quarck"
          flex={1}
          alignSelf="stretch"
        >
          <Text
            fontFamily="$mulishRegular"
            color="$neutral-800"
            fontSize="$font-size-xxs"
            numberOfLines={3}
            width="90%"
          >
            {lessonTitle}
          </Text>
          <XStack gap="$spacing-inset-quarck" alignItems="center">
            {renderLessonStatus}
          </XStack>
        </YStack>
      </XStack>
    </View>
  )
}
